// src/utils/dentallyZones.js
//
// Zones are what Dentally's UI calls the recurring blocks a practice sets
// aside for a kind of work — Exams 08:00-13:00, Treatment 14:00-17:00, a
// 30-minute NHS slot, an Emergency hour. Their API calls the same thing
// `session_appointments` and never uses the word "zone", which is why this
// went unnoticed for so long.
//
// WHY THIS EXISTS. Dentally's availability is the rota minus booked
// appointments and nothing else. Verified against their sandbox: a
// practitioner with an Emergency zone at 11:00-12:00 still returns one
// unbroken 09:00-13:00 free period. So the picker can offer a routine
// treatment inside an emergency-only hour and the booking will succeed.
//
// These helpers answer "what is this free time actually FOR" so the picker can
// say so. They never remove slots: Dentally does not enforce zones either
// (`display_type` is a background render), the practice follows them by eye,
// and silently deleting bookable time because of a hint would be worse than
// the problem.

/** Tolerant ISO parse — the provider sends `2026-10-15T11:00:00.000+01:00`. */
const toTime = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/** Local calendar day key, so a zone lands on the day staff see it on. */
export const dayKeyOf = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // Local parts, never toISOString(): that converts to UTC and moves a late
  // evening slot onto the next day for anyone east of Greenwich.
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const practitionerOf = (slotOrZone) => (
  slotOrZone?._queried_practitioner_id
  ?? slotOrZone?.practitioner_id
  ?? (typeof slotOrZone?.practitioner === 'object' ? slotOrZone?.practitioner?.id : slotOrZone?.practitioner)
  ?? null
);

/**
 * Does this zone apply to this slot?
 *
 * Two conditions, and BOTH matter:
 *  - the same practitioner, because a zone belongs to one person's diary and
 *    saying "this is an Exams slot" about a different clinician is just wrong;
 *  - a genuine time overlap, half-open, so a zone finishing at 12:00 does not
 *    claim the 12:00 slot that starts as it ends.
 */
export const zoneAppliesToSlot = (zone, slot) => {
  if (!zone || !slot) return false;
  const zp = practitionerOf(zone);
  const sp = practitionerOf(slot);
  if (zp != null && sp != null && String(zp) !== String(sp)) return false;

  const zs = toTime(zone.start_time);
  const zf = toTime(zone.finish_time);
  const ss = toTime(slot.start_time);
  const sf = toTime(slot.finish_time) ?? ss;
  if (zs == null || zf == null || ss == null) return false;

  // Half-open on both sides: touching edges are not an overlap.
  return ss < zf && (sf ?? ss) > zs;
};

/** The zone covering a slot, or null. First match wins — zones rarely overlap. */
export const zoneForSlot = (zones, slot) => (
  (zones || []).find((z) => zoneAppliesToSlot(z, slot)) || null
);

/**
 * Zones on one local day, earliest first, de-duplicated by label+window.
 *
 * A zone recurs weekly, so the same label can arrive once per practitioner.
 * The day header wants "Exams 08:00-13:00" said once, not six times.
 *
 * `practitionerIds` restricts the band to the diaries being shown. Omit it and
 * every practitioner's zones are banded, which is only correct when the caller
 * has already narrowed the list.
 */
export const zonesForDay = (zones, dayKey, practitionerIds) => {
  // Scope to the practitioners whose slots are actually on screen. Zones come
  // back for the whole practice, but practitioners are SITE-scoped while
  // session_appointments are not, so the raw list can contain diaries this
  // practice cannot even book into. Banding those over a day would describe it
  // by someone else's rota - and on a multi-practitioner day it would merge
  // unrelated diaries ("Exams" from one, "Emergency" from another) and present
  // the result as what the day is for.
  const wanted = practitionerIds == null
    ? null
    : new Set([...practitionerIds].filter((id) => id != null).map(String));
  const onDay = (zones || []).filter((z) => {
    if (dayKeyOf(z.start_time) !== dayKey) return false;
    if (!wanted) return true;
    const zp = z.practitioner_id;
    return zp != null && wanted.has(String(zp));
  });
  const seen = new Set();
  const unique = [];
  onDay.forEach((z) => {
    const key = `${z.label || ''}|${z.start_time}|${z.finish_time}`;
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(z);
  });
  return unique.sort((a, b) => (toTime(a.start_time) || 0) - (toTime(b.start_time) || 0));
};

/** "08:00–13:00" in the viewer's locale, for the band and the hover text. */
export const formatZoneWindow = (zone) => {
  const fmt = (v) => {
    const d = v ? new Date(v) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };
  const a = fmt(zone?.start_time);
  const b = fmt(zone?.finish_time);
  return a && b ? `${a}–${b}` : a || '';
};

/**
 * One sentence for the Confirm step, or null when the slot is unzoned.
 *
 * Deliberately a statement rather than a question: Dentally does not enforce
 * zones, so this must not read as a block. It names the time and the zone and
 * leaves the decision with the operator.
 */
export const describeZoneConflict = (zones, slot) => {
  const zone = zoneForSlot(zones, slot);
  if (!zone) return null;
  const label = (zone.label || '').trim();
  const when = formatZoneWindow(zone);
  if (!label) return `This time sits inside a reserved block${when ? ` (${when})` : ''} in Dentally.`;
  return `This time sits inside the ${label} block${when ? ` (${when})` : ''} for this practitioner in Dentally.`;
};
